import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { nextFromSearch } from '../lib/post-login-target';
import { Eye, EyeOff } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import fieldiqWordmark from '../assets/fieldiq-wordmark.png';
import fieldiqWordmarkLight from '../assets/fieldiq-wordmark-light.png';

export default function LoginScreen() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  // The page a signed-out link asked for (the ticket-reminder SMS), if any.
  const next = nextFromSearch(useLocation().search);
  const wordmark = theme === 'light' ? fieldiqWordmarkLight : fieldiqWordmark;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); // This stops the page from refreshing!
    e.stopPropagation(); // Extra protection
    
    setError('');
    setIsLoading(true);

    try {
      const { user, company } = await login(email.trim(), password);
      const isTrucking = company?.enabledVerticals?.includes('trucking') || company?.enabled_verticals?.includes('trucking');

      if (next) {
        navigate(next, { replace: true });
      } else if (user.role === 'field_worker' || user.role === 'driver') {
        navigate(isTrucking ? '/driver' : '/field/home');
      } else {
        navigate('/');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
        setError('Cannot reach server. Please check your connection and try again.');
      } else {
        const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Login failed. Check your credentials.';
        setError(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-fieldiq-dark flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src={wordmark}
            alt="FieldIQ"
            className="h-16 sm:h-20 w-auto max-w-full mx-auto"
          />
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-fieldiq-danger/20 border border-fieldiq-danger text-fieldiq-danger px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Email</label>
            <input
              type="email"
              name="email"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-fieldiq-card border border-fieldiq-border rounded-lg px-4 py-3 text-white focus:border-fieldiq-accent focus:outline-none"
              placeholder="you@company.com"
              required
            />
          </div>

          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-fieldiq-card border border-fieldiq-border rounded-lg px-4 py-3 text-white focus:border-fieldiq-accent focus:outline-none"
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-fieldiq-text"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-fieldiq-accent text-fieldiq-dark font-bold py-3 rounded-lg hover:bg-fieldiq-accent/90 transition disabled:opacity-50"
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>

          <div className="text-center mt-3">
            <Link
              to="/forgot-password"
              className="text-fieldiq-accent text-sm hover:underline"
            >
              Forgot password?
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}